<script setup lang="ts">
import { computed } from 'vue'

import { useReportMedia } from '../../composables/useReportMedia'
import type { ReportColumn } from '../../models/column.types'
import type { ReportFilter } from '../../models/filter.types'
import type { ReportRow } from '../../models/report.types'
import {
  orderCardColumns,
  presentReportMediaStatus,
  resolveReportMediaDisplayRow,
} from '../../service/reportMediaPresentation'

import ReportMediaMetrics from './ReportMediaMetrics.vue'

import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui/icon'
import { PlatformLogo, resolveStaticPlatformLogo } from '@/shared/ui'
import { MediaViewer, PreviewImage, PreviewVideo, type MediaItem } from '@/shared/ui/media'

// One creative tile — v3 `OrphexMediaCard.vue` (184 ln) and `OrphexAdLibraryCard.vue` (107 ln)
// collapsed into one component with a `variant` prop, as ORP-18651 AC2 requires. The two v3 files
// differed only in column ordering and the header chrome; `orderCardColumns` owns the former and
// this tile keeps the latter (channel logo + status chip) aligned with the active carousel slide.
//
// The prop surface is UNCHANGED from the shipped component so the cross-feature importer
// (`features/summary/components/CreativeSliderCard.vue`, via the reports barrel) keeps compiling —
// `variant` is additive and defaults to the plain tile.
const props = withDefaults(
  defineProps<{
    row: ReportRow
    dimensions: readonly ReportColumn[]
    metrics: readonly ReportColumn[]
    /** Marketing channel this surface is pinned to — a logo-less metric borrows it. */
    channel?: string
    mediaType?: 'Image' | 'Video'
    alt: string
    unavailableLabel: string
    variant?: 'default' | 'adLibrary'
    /** `sunken` drops the outline for a filled tile (Summary's creative slider). */
    surface?: 'outlined' | 'sunken'
    filterable?: boolean
  }>(),
  { mediaType: 'Image', variant: 'default', channel: '', filterable: false, surface: 'outlined' },
)
const emit = defineEmits<{ 'add-filter': [filter: ReportFilter] }>()

const { t } = useI18n()
const {
  assets,
  active,
  activeIndex,
  previous,
  next,
  broken,
  markBroken,
  viewerOpen,
  openViewer,
  closeViewer,
} = useReportMedia(
  () => props.row,
  () => (props.mediaType === 'Video' ? 'video' : 'image'),
)

const cardDimensions = computed(() => orderCardColumns(props.dimensions, 'dimension'))
const cardMetrics = computed(() => orderCardColumns(props.metrics, 'metric'))

/** More than one creative on the row means a breakdown gallery — the only MediaViewer case. */
const isGallery = computed(() => assets.value.length > 1)
const viewerItems = computed<MediaItem[]>(() =>
  assets.value.map((asset) => ({
    src: asset.src,
    type: asset.kind,
    alt: props.alt,
    thumbnail: asset.thumbnail,
  })),
)
const activeRow = computed<ReportRow>(() =>
  resolveReportMediaDisplayRow(props.row, active.value?.source, activeIndex.value, isGallery.value),
)
const platform = computed(() => {
  const value = activeRow.value['marketing_channel']
  return typeof value === 'string' ? value.trim() : ''
})
const platformLogo = computed(() => {
  const src = resolveStaticPlatformLogo(platform.value)
  return {
    platform: platform.value,
    alt: platform.value,
    size: 20,
    showText: true,
    ...(src ? { src } : {}),
  }
})
const status = computed(() => presentReportMediaStatus(activeRow.value['effective_status']))
const statusLabel = computed(() => {
  const value = status.value
  const label = value?.labelKey
    ? t(`reports.card.status.${value.labelKey}`)
    : (value?.fallbackLabel ?? '')
  return label.toUpperCase()
})
</script>

<template>
  <article
    class="report-media-item flex h-full flex-col overflow-hidden p-xs rounded-md gap-md"
    :class="[
      `report-media-item--${variant}`,
      surface === 'sunken' ? 'bg-surface-sunken' : 'border border-border bg-surface',
    ]"
  >
    <div class="report-media-item__stage relative w-full">
      <button
        v-if="active && !broken"
        type="button"
        class="report-media-item__preview group flex w-full h-51 flex-col items-center justify-center p-0 border-0 gap-2xs bg-transparent text-muted cursor-pointer"
        :aria-label="alt"
        @click="openViewer"
      >
        <!-- v3 `OrphexPreviewMedia.vue:11-17`: a primary scrim with the media-kind glyph fades in
             over the creative itself (not the whole stage) on hover or keyboard focus. -->
        <span class="relative inline-flex h-51 max-w-full overflow-hidden rounded-md">
          <img
            class="h-51 w-auto max-w-full object-contain object-center"
            :src="active.thumbnail || active.src"
            :alt="alt"
            @error="markBroken"
          />
          <span
            class="absolute inset-0 flex items-center justify-center bg-primary/70 text-primary-fg opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
            data-testid="report-media-hover"
            aria-hidden="true"
          >
            <Icon :name="active.kind === 'video' ? 'video' : 'image'" :size="30" />
          </span>
        </span>
        <span
          v-if="active.kind === 'video'"
          class="report-media-item__play absolute flex transition-opacity group-hover:opacity-0 group-focus-visible:opacity-0 w-11 h-11 items-center justify-center rounded-full bg-media-control text-on-media-strong"
          data-testid="report-media-play"
          aria-hidden="true"
        >
          <Icon name="play" :size="20" />
        </span>
        <span
          v-if="isGallery"
          class="report-media-item__count absolute right-xs bottom-xs py-0 px-2xs rounded-sm bg-media-control text-on-media-strong text-[0.6875rem]"
        >
          {{ t('reports.card.galleryCount', { count: assets.length }) }}
        </span>
      </button>
      <div
        v-else
        class="report-media-item__broken flex w-full h-51 flex-col items-center justify-center p-0 border-0 gap-2xs bg-transparent text-muted text-[0.75rem] text-center"
        role="img"
        data-testid="report-media-broken"
        :aria-label="unavailableLabel"
      >
        <Icon name="image-slash" :size="32" />
        <span>{{ unavailableLabel }}</span>
      </div>

      <button
        v-if="isGallery"
        type="button"
        class="report-media-item__carousel report-media-item__carousel--previous absolute top-1/2 z-1 grid w-7 h-7 p-0 place-items-center transform-[translateY(-50%)] border-0 rounded-full bg-surface text-primary shadow-card cursor-pointer text-xl leading-none [&:disabled]:cursor-default [&:disabled]:opacity-70 left-sm"
        :disabled="activeIndex === 0"
        :aria-label="t('reports.card.previous')"
        data-testid="report-media-previous"
        @click.stop="previous"
      >
        <Icon name="chevron-left" :size="16" />
      </button>
      <button
        v-if="isGallery"
        type="button"
        class="report-media-item__carousel report-media-item__carousel--next absolute top-1/2 z-1 grid w-7 h-7 p-0 place-items-center transform-[translateY(-50%)] border-0 rounded-full bg-surface text-primary shadow-card cursor-pointer text-xl leading-none [&:disabled]:cursor-default [&:disabled]:opacity-70 right-sm"
        :disabled="activeIndex === assets.length - 1"
        :aria-label="t('reports.card.next')"
        data-testid="report-media-next"
        @click.stop="next"
      >
        <Icon name="chevron-right" :size="16" />
      </button>
    </div>

    <div
      v-if="variant === 'default' && (platform || status)"
      class="report-media-item__header flex min-h-5 items-center justify-between gap-sm"
    >
      <PlatformLogo v-if="platform" v-bind="platformLogo" />
      <span
        v-if="status"
        class="report-media-item__status flex w-max justify-center py-0.5 px-2 rounded-md text-2xs font-bold"
        :class="`report-media-item__status--${status.tone}`"
        data-testid="report-media-status"
        >{{ statusLabel }}</span
      >
    </div>

    <ReportMediaMetrics
      :row="activeRow"
      :dimensions="cardDimensions"
      :metrics="cardMetrics"
      :channel="channel"
      :filterable="filterable"
      @add-filter="emit('add-filter', $event)"
    />

    <MediaViewer
      v-if="viewerOpen && isGallery"
      :items="viewerItems"
      :index="activeIndex"
      :close-label="t('reports.actions.close')"
      :prev-label="t('reports.card.previous')"
      :next-label="t('reports.card.next')"
      :retry-label="t('common.retry')"
      :load-error-message="unavailableLabel"
      @close="closeViewer"
    />
    <!-- A direct video renders with `controls`, so a browser that blocks autoplay still leaves the
         user a way to play it (contract 02 §14 — the module-02 owned fallback). -->
    <PreviewVideo
      v-else-if="viewerOpen && active?.kind === 'video'"
      v-model="viewerOpen"
      :src="active.src"
      :thumbnail="active.thumbnail"
      :alt="alt"
      :close-label="t('reports.actions.close')"
    />
    <PreviewImage
      v-else-if="viewerOpen && active"
      v-model="viewerOpen"
      :src="active.src"
      :alt="alt"
      :rotate-left-label="t('reports.card.rotateLeft')"
      :rotate-right-label="t('reports.card.rotateRight')"
      :zoom-in-label="t('reports.card.zoomIn')"
      :zoom-out-label="t('reports.card.zoomOut')"
      :close-label="t('reports.actions.close')"
    />
  </article>
</template>

<style scoped>
.report-media-item .report-media-item__status {
  border: 1px solid var(--ui-color-status-neutral-fg);
  color: var(--ui-color-status-neutral-fg);
  background-color: var(--ui-color-status-neutral-bg);
}
.report-media-item .report-media-item__status--success {
  border-color: var(--ui-color-status-active-fg);
  color: var(--ui-color-status-active-fg);
  background-color: var(--ui-color-status-active-bg);
}
.report-media-item .report-media-item__status--warning {
  border-color: var(--ui-color-status-paused-fg);
  color: var(--ui-color-status-paused-fg);
  background-color: var(--ui-color-status-paused-bg);
}
.report-media-item .report-media-item__status--danger {
  border-color: var(--ui-color-status-removed-fg);
  color: var(--ui-color-status-removed-fg);
  background-color: var(--ui-color-status-removed-bg);
}
</style>
